// src/services/supabase.js
import { readRequestDevice } from './requestDevice';
import { createClient } from '@supabase/supabase-js';

// 환경 변수 가져오기
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

// 환경 변수 확인
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('앱 설정이 올바르지 않습니다. 관리자에게 문의하세요.');
}

// Attach the member credential at request time, including after entry without a reload.
// Public/publishable API keys identify the app; the DB still validates this session.
export const clubFetch = async (input, init = {}) => {
  const headers = new Headers(init.headers);
  const token = localStorage.getItem('tapAccessToken');
  if (token) headers.set('x-access-token', token);
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (init.signal?.aborted) controller.abort();
  else init.signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 15000);
  try { return await fetch(input, { ...init, headers, signal: controller.signal }); }
  finally { clearTimeout(timeout); init.signal?.removeEventListener('abort', abort); }
};

// Supabase 클라이언트 생성 (개선된 옵션)
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      storageKey: 'tap-rating-auth',
      flowType: 'pkce'
    },
    global: {
      fetch: clubFetch,
      headers: {
        'x-application-name': 'tap-rating-system'
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Request forms do not inherit an old personal-account session.
export const guestRequestClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co', supabaseAnonKey || 'placeholder-key', {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: 'tap-guest-requests' },
    global: { fetch: (input, init = {}) => {
      const headers = new Headers(init.headers);
      const device = readRequestDevice();
      if (device) headers.set('x-request-device', device);
      return clubFetch(input, { ...init, headers });
    } }
  }
);

// 연결 상태 확인 함수
export const checkSupabaseConnection = async () => {
  try {
    const { error } = await supabase
      .from('players')
      .select('count')
      .limit(1)
      .single();
    return !error;
  } catch {
    return false;
  }
};

// === 관리자 인증 클라이언트 ===
let _adminClient = null;
let _verifyCache = { result: null, timestamp: 0 };
const VERIFY_CACHE_TTL = 30000; // 30초간 캐시 유지
let _verifyInFlight = null; // in-flight 디듀플리케이션

const createAdminSupabaseClient = (token) => {
  return createClient(
    supabaseUrl || 'https://placeholder.supabase.co',
    supabaseAnonKey || 'placeholder-key',
    {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: window.localStorage,
        storageKey: 'tap-rating-auth',
        flowType: 'pkce'
      },
      global: {
        fetch: clubFetch,
        headers: {
          'x-application-name': 'tap-rating-system',
          'x-admin-token': token
        }
      },
      db: {
        schema: 'public'
      }
    }
  );
};

// 관리자 토큰 설정 (로그인 시 호출)
export const setAdminToken = (token) => {
  if (token) {
    sessionStorage.setItem('tapAdminToken', token);
    sessionStorage.setItem('tapAdminMode', 'true');
    _adminClient = createAdminSupabaseClient(token);
    // 캐시 워밍업: 토큰이 방금 verify_admin_code로 검증됨 → 즉시 캐시
    _verifyCache = { result: true, timestamp: Date.now() };
  } else {
    sessionStorage.removeItem('tapAdminToken');
    sessionStorage.removeItem('tapAdminMode');
    _adminClient = null;
    _verifyCache = { result: null, timestamp: 0 };
  }
};

// 관리자 토큰 존재 여부 동기 확인
export const hasAdminToken = () => {
  return !!sessionStorage.getItem('tapAdminToken');
};

// 관리자 세션 해제 (로그아웃 시 호출)
export const clearAdminSession = () => {
  const token = sessionStorage.getItem('tapAdminToken');
  if (token) {
    (async () => { try { await supabase.rpc('revoke_admin_session', { input_token: token }); } catch {} })();
  }
  sessionStorage.removeItem('tapAdminToken');
  sessionStorage.removeItem('tapAdminMode');
  sessionStorage.removeItem('adminBlocked');
  _adminClient = null;
  _verifyCache = { result: null, timestamp: 0 };
};

// 관리자 세션 검증 (앱 로드 시 호출)
// 반환값: 'valid' | 'invalid' | 'error'
//   'valid'   - 서버 확인 완료
//   'invalid' - 서버가 토큰 거부 (만료/취소)
//   'error'   - 네트워크 오류 (세션 유지)
export const verifyAdminSession = async (force = false) => {
  const token = sessionStorage.getItem('tapAdminToken');
  if (!token) return 'invalid';

  // 최근 검증 성공 결과가 있으면 캐시 반환
  const now = Date.now();
  if (!force && _verifyCache.result === true && now - _verifyCache.timestamp < VERIFY_CACHE_TTL) {
    return 'valid';
  }

  // in-flight 디듀플리케이션: 동시 호출 시 같은 Promise 공유
  if (_verifyInFlight) return _verifyInFlight;

  _verifyInFlight = (async () => {
    try {
      const { data, error } = await supabase.rpc('verify_admin_session', {
        input_token: token
      });
      if (error) {
        return 'error';
      }
      if (data === true) {
        _verifyCache = { result: true, timestamp: Date.now() };
        return 'valid';
      }
      // 서버가 명확히 거부: 토큰이 무효
      return 'invalid';
    } catch {
      return 'error';
    } finally {
      _verifyInFlight = null;
    }
  })();

  return _verifyInFlight;
};

// 관리자 클라이언트 가져오기 (쓰기 작업용)
// 토큰이 없으면 throw — 호출자가 admin 권한 없이 실행하는 것을 방지
// #10: 세션 만료 시에도 즉시 차단
export const getAdminClient = () => {
  const token = sessionStorage.getItem('tapAdminToken');
  if (!token) {
    _adminClient = null;
    throw new Error('관리자 인증이 필요합니다. 관리자 로그인 후 다시 시도하세요.');
  }

  // 캐시된 검증 결과가 만료됐으면 클라이언트 재생성하지 않음 (비동기 검증은 별도)
  if (!_adminClient) {
    _adminClient = createAdminSupabaseClient(token);
  }
  return _adminClient;
};

// #10: 비동기 admin 세션 검증 (쓰기 작업 전 호출)
export const ensureAdminSession = async () => {
  const result = await verifyAdminSession(true);

  if (result === 'valid') {
    return getAdminClient();
  }

  if (result === 'invalid') {
    clearAdminSession();
    throw new Error('관리자 세션이 만료되었습니다. 다시 로그인해주세요.');
  }

  // An unavailable verifier must not authorize a new write. Preserve the token for retry.
  throw new Error('운영진 인증을 확인할 수 없습니다. 연결 상태를 확인한 뒤 다시 시도해 주세요.');
};